import type { FilterStage } from '../state/filterStore';
import type { Vertex } from './pointInPolygon';

export const SCHEMA_VERSION = 1;

// localStorage quota is ~5 MB per origin. A baseSelection of 100k uint32 ids
// serializes to roughly 700 kB of JSON; beyond that we fall back to storing
// the lasso polygon and recomputing the index set from blob positions on load.
export const BASE_SELECTION_CAP = 100_000;

export type WidgetLayout = { x: number; y: number; w: number; h: number };

export type Widget = {
  id: string;
  chartTypeId: string;
  slots: Record<string, string | null>;
  layout: WidgetLayout;
  title?: string;
  content?: string;
};

export type Dashboard = {
  id: string;
  name: string;
  createdAt: string;
  filterStages: FilterStage[];
  baseSelection: Uint32Array | null;
  polygon: Vertex[] | null;
  widgets: Widget[];
};

export type SerializedDashboard = {
  id: string;
  name: string;
  createdAt: string;
  filterStages: FilterStage[];
  baseSelection: number[] | null;
  polygon: Vertex[] | null;
  widgets: Widget[];
};

export type PersistedDashboards = {
  schemaVersion: number;
  dashboards: SerializedDashboard[];
};

export function serializeDashboard(dashboard: Dashboard): SerializedDashboard {
  let baseSelection: number[] | null = null;
  if (dashboard.baseSelection !== null) {
    if (dashboard.baseSelection.length <= BASE_SELECTION_CAP) {
      baseSelection = Array.from(dashboard.baseSelection);
    } else if (dashboard.polygon !== null && dashboard.polygon.length >= 3) {
      // Too large to persist; the polygon is enough to reconstruct on load.
      baseSelection = null;
    } else {
      console.warn(
        `[dashboardStore] dropping baseSelection of ${dashboard.baseSelection.length} ids ` +
          `for dashboard ${dashboard.id}: above ${BASE_SELECTION_CAP} cap and no polygon to reconstruct from.`,
      );
      baseSelection = null;
    }
  }
  return {
    id: dashboard.id,
    name: dashboard.name,
    createdAt: dashboard.createdAt,
    filterStages: dashboard.filterStages,
    baseSelection,
    polygon: dashboard.polygon,
    widgets: dashboard.widgets,
  };
}

export function deserializeDashboard(raw: SerializedDashboard): Dashboard {
  return {
    id: raw.id,
    name: raw.name,
    createdAt: raw.createdAt,
    filterStages: raw.filterStages,
    baseSelection:
      raw.baseSelection === null || raw.baseSelection === undefined
        ? null
        : Uint32Array.from(raw.baseSelection),
    polygon: raw.polygon ?? null,
    widgets: raw.widgets,
  };
}

export function newDashboardId(): string {
  return `dash-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function newWidgetId(): string {
  return `widget-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
